export const FBX_TIME_ZONE = "America/New_York";

type DateParts = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
};

const easternDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: FBX_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const easternDateTimeFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: FBX_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  hourCycle: "h23",
});

const getParts = (formatter: Intl.DateTimeFormat, date: Date = new Date()): DateParts => {
  const parts = formatter.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    year: parts.year || "0000",
    month: parts.month || "00",
    day: parts.day || "00",
    hour: parts.hour || "00",
    minute: parts.minute || "00",
    second: parts.second || "00",
  };
};

export const getEasternDateParts = (date: Date = new Date()): DateParts =>
  getParts(easternDateTimeFormatter, date);

export const getTodayEasternYMD = (date: Date = new Date()): string => {
  const parts = getParts(easternDateFormatter, date);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const getEasternSqlDateTime = (date: Date = new Date()): string => {
  const parts = getEasternDateParts(date);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

export const formatLocalYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseYMDDate = (value: unknown): Date | null => {
  const raw = String(value || "").trim();
  const dateOnly = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;

  const [year, month, day] = dateOnly.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
};

export const formatEasternDisplayDate = (
  value: unknown,
  locale = "en-CA"
): string => {
  if (!value || value === "-") return "-";

  const raw = String(value).trim();
  const dateOnly = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;

  const numericDate = Number(raw);
  const date =
    !Number.isNaN(numericDate) && numericDate > 0
      ? new Date(numericDate < 9999999999 ? numericDate * 1000 : numericDate)
      : new Date(raw);

  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat(locale, {
    timeZone: FBX_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};
